/**
 * Configuration - Jeu Chrono 10.00
 * Page de configuration du jeu du chronomètre
 * MIGRATION FIREBASE: Utilise Firebase direct au lieu de l'API Python
 */
import React, { useState, useEffect } from 'react';
import { chronoGameService, productsService } from '../services/firebaseService';

const ChronoGameConfig = () => {
  const [config, setConfig] = useState({
    enabled: false,
    chances_per_day: 3,
    wins_per_day: 1,
    target_time: 10.00,
    tolerance: 1.0,           // FIX Build 202: Défaut 1.0s (anti-triche)
    win_tolerance: 0.1,       // FIX Build 202: Tolérance pour gagner
    reward_expiry_days: 7,
    min_order_amount: 0,
    reward_type: 'product',   // FIX Build 202: À la racine pour CF
    wallet_amount: 5,         // FIX Build 202: Montant si type wallet
    test_mode_enabled: false, // Mode test: permet de jouer plusieurs fois
    test_plays_per_day: 10,   // Nombre de parties en mode test
    reward: {
      type: 'product',
      product_id: '',
      product_name: '',
      value: 0
    }
  });

  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState(null);
  const [playerStats, setPlayerStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [searchProduct, setSearchProduct] = useState('');

  // Filtrer les produits par recherche
  const filteredProducts = products.filter(p =>
    p.name.toLowerCase().includes(searchProduct.toLowerCase())
  );

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // FIREBASE DIRECT: Charger config, produits et stats en parallèle
      const [configRes, productsRes, statsRes, playerStatsRes] = await Promise.all([
        chronoGameService.getConfig(),
        productsService.getAll(),
        chronoGameService.getStats(),
        chronoGameService.getPlayerStats()
      ]);

      if (configRes.config) {
        setConfig({ ...config, ...configRes.config });
      }

      setProducts(productsRes.products || []);
      setStats(statsRes);
      setPlayerStats(playerStatsRes);
      setError(null);
    } catch (err) {
      console.log('Config not found, using defaults:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // Validation avant sauvegarde
      if ((config.reward_type || 'product') === 'product' && !config.reward?.product_id) {
        setError('Veuillez sélectionner un produit récompense');
        setSaving(false);
        return;
      }
      if (config.reward_type === 'wallet' && (!config.wallet_amount || config.wallet_amount <= 0)) {
        setError('Le montant du crédit fidélité doit être supérieur à 0');
        setSaving(false);
        return;
      }

      // FIX Build 202: Format PLAT pour compatibilité Cloud Functions
      // NE PAS inclure l'objet imbriqué "reward" - CF attend des champs à la racine
      const configToSave = {
        enabled: config.enabled,
        hidden: config.hidden || false,
        target_time: config.target_time || 10.0,
        tolerance: config.tolerance || 1.0,
        win_tolerance: config.win_tolerance ?? 0.1,
        chances_per_day: config.chances_per_day || 3,
        wins_per_day: config.wins_per_day || 1,
        reward_expiry_days: config.reward_expiry_days || 7,
        min_order_amount: config.min_order_amount || 0,
        // Type de récompense à la RACINE
        reward_type: config.reward_type || 'product',
        // Valeur selon le type
        reward_value: config.reward_type === 'wallet'
          ? (config.wallet_amount || 0)
          : (config.reward?.value || 0),
        reward_product_id: config.reward?.product_id || null,
        reward_product_name: config.reward?.product_name || null,
        // wallet_amount stocké pour édition future
        wallet_amount: config.wallet_amount || 0,
        // Mode test
        test_mode_enabled: config.test_mode_enabled || false,
        test_plays_per_day: config.test_plays_per_day || 10,
      };

      // FIREBASE DIRECT: Sauvegarder dans settings/chrono_game
      await chronoGameService.updateConfig(configToSave);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError('Erreur lors de la sauvegarde: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleProductChange = (productId) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      setConfig({
        ...config,
        reward: {
          ...config.reward,
          product_id: product.id,
          product_name: product.name,
          value: product.base_price || product.price || 0
        }
      });
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          <span className="text-4xl mr-2">⏱️</span> Chrono 10.00
        </h1>
        <p className="text-gray-600">
          Jeu du chronomètre - Arrête-toi pile à 10 secondes pour gagner !
        </p>
      </div>

      {/* Stats rapides */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <p className="text-sm text-blue-600">Parties aujourd'hui</p>
            <p className="text-2xl font-bold text-blue-800">{stats.today?.plays || 0}</p>
          </div>
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <p className="text-sm text-green-600">Victoires aujourd'hui</p>
            <p className="text-2xl font-bold text-green-800">{stats.today?.wins || 0}</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
            <p className="text-sm text-purple-600">Taux de victoire</p>
            <p className="text-2xl font-bold text-purple-800">{stats.today?.win_rate || 0}%</p>
          </div>
          <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
            <p className="text-sm text-orange-600">Total parties</p>
            <p className="text-2xl font-bold text-orange-800">{stats.total?.plays || 0}</p>
          </div>
        </div>
      )}

      {/* Stats joueurs : classement + meilleurs temps + derniers joueurs (avec noms) */}
      {playerStats && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold mb-1">🏆 Joueurs &amp; Classement</h2>
          <p className="text-sm text-gray-500 mb-1">{playerStats.uniquePlayers || 0} joueur(s) unique(s) ont joué au Chrono</p>
          <p className="text-xs text-gray-400 mb-4">🎯 <strong>10s</strong> = a atteint 10,00s · 🎁 <strong>Cadeau</strong> = a réellement reçu une récompense · <span className="text-orange-600">« 10s (limite) »</span> = a touché 10s mais la limite de gains/jour était atteinte (aucun cadeau).</p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Classement par activité */}
            <div>
              <h3 className="font-semibold text-gray-700 mb-2">Classement (parties jouées)</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b">
                      <th className="py-2">#</th><th className="py-2">Joueur</th>
                      <th className="py-2 text-center" title="Fois où le joueur a atteint 10,00s">🎯 10s</th>
                      <th className="py-2 text-center" title="Cadeaux réellement reçus (limite déduite)">🎁 Cadeaux</th>
                      <th className="py-2 text-center">Parties</th>
                      <th className="py-2 text-right">Meilleur écart</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(playerStats.leaderboard || []).map((p, i) => (
                      <tr key={i} className="border-b border-gray-100">
                        <td className="py-2 text-gray-400">{i + 1}</td>
                        <td className="py-2 font-medium text-gray-800">{p.name}</td>
                        <td className="py-2 text-center">{p.wins > 0 ? <span className="text-blue-600 font-semibold">{p.wins}</span> : '0'}</td>
                        <td className="py-2 text-center">{p.rewards > 0 ? <span className="text-green-600 font-bold">{p.rewards}</span> : '0'}</td>
                        <td className="py-2 text-center text-gray-500">{p.plays}</td>
                        <td className="py-2 text-right text-gray-600">{p.bestDiff != null ? `±${p.bestDiff.toFixed(2)}s` : '—'}</td>
                      </tr>
                    ))}
                    {(playerStats.leaderboard || []).length === 0 && (
                      <tr><td colSpan="6" className="py-4 text-center text-gray-400">Aucune partie</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Meilleurs temps */}
            <div>
              <h3 className="font-semibold text-gray-700 mb-2">Meilleurs temps (proches de 10,00s)</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b">
                      <th className="py-2">#</th><th className="py-2">Joueur</th>
                      <th className="py-2 text-center">Temps</th><th className="py-2 text-right">Écart</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(playerStats.bestTimes || []).map((p, i) => (
                      <tr key={i} className="border-b border-gray-100">
                        <td className="py-2 text-gray-400">{i + 1}</td>
                        <td className="py-2 font-medium text-gray-800">{p.name}{p.rewarded && <span className="ml-1">🏆</span>}</td>
                        <td className="py-2 text-center font-mono">{p.time?.toFixed(2)}s</td>
                        <td className="py-2 text-right text-gray-600">±{Math.abs((p.time || 0) - 10).toFixed(2)}s</td>
                      </tr>
                    ))}
                    {(playerStats.bestTimes || []).length === 0 && (
                      <tr><td colSpan="4" className="py-4 text-center text-gray-400">Aucune partie</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Derniers joueurs */}
          <h3 className="font-semibold text-gray-700 mt-6 mb-2">Derniers joueurs</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2">Joueur</th><th className="py-2 text-center">Temps</th>
                  <th className="py-2 text-center">Résultat</th><th className="py-2 text-right">Date</th>
                </tr>
              </thead>
              <tbody>
                {(playerStats.recent || []).map((p, i) => (
                  <tr key={i} className="border-b border-gray-100">
                    <td className="py-2 font-medium text-gray-800">{p.name}</td>
                    <td className="py-2 text-center font-mono">{p.time != null ? `${p.time.toFixed(2)}s` : '—'}</td>
                    <td className="py-2 text-center">
                      {p.rewarded
                        ? <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-semibold">Cadeau 🎁</span>
                        : p.won
                          ? <span className="px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 text-xs font-semibold" title="A atteint 10s mais limite de gains/jour atteinte — aucun cadeau">🎯 10s (limite)</span>
                          : <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-xs">Perdu</span>}
                    </td>
                    <td className="py-2 text-right text-gray-500">
                      {p.date ? new Date(p.date).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                    </td>
                  </tr>
                ))}
                {(playerStats.recent || []).length === 0 && (
                  <tr><td colSpan="4" className="py-4 text-center text-gray-400">Aucune partie enregistrée</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Success Message */}
      {saved && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 text-green-800">
          ✅ Configuration sauvegardée avec succès
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          ❌ {error}
        </div>
      )}

      <div className="space-y-6">
        {/* Activation */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">🎮 Activation du Jeu</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Jeu Chrono 10.00</p>
                <p className="text-sm text-gray-600">Activer ou désactiver le jeu pour tous les utilisateurs</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.enabled}
                  onChange={(e) => setConfig({...config, enabled: e.target.checked})}
                  className="sr-only peer"
                />
                <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-indigo-600"></div>
              </label>
            </div>

            {/* Option Cacher - avec feedback visuel clair */}
            <div className={`p-4 rounded-lg border-2 transition-colors ${config.hidden ? 'bg-red-50 border-red-300' : 'bg-green-50 border-green-300'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-700">🙈 Cacher le jeu sur l'app</p>
                  <p className="text-sm text-gray-500">Le bouton sera invisible même si le jeu est activé</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.hidden || false}
                    onChange={(e) => setConfig({...config, hidden: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-gray-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-red-500"></div>
                </label>
              </div>
              <p className={`text-sm font-medium mt-2 ${config.hidden ? 'text-red-700' : 'text-green-700'}`}>
                {config.hidden
                  ? '❌ Le jeu est INVISIBLE sur l\'app (même si activé)'
                  : '✅ Le jeu est VISIBLE sur l\'app'}
              </p>
            </div>

            {/* Mode Test */}
            <div className="flex items-center justify-between p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <div>
                <p className="font-medium text-orange-800">🧪 Mode Test</p>
                <p className="text-sm text-orange-600">Permet de rejouer immédiatement sans attendre (pour les tests)</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.test_mode_enabled}
                  onChange={(e) => setConfig({...config, test_mode_enabled: e.target.checked})}
                  className="sr-only peer"
                />
                <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-orange-500"></div>
              </label>
            </div>
            {config.test_mode_enabled && (
              <>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-700 font-medium">
                    ⚠️ Mode Test ACTIVÉ - N'oubliez pas de le désactiver en production !
                  </p>
                </div>
                <div className="flex items-center gap-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium text-orange-800">🔢 Parties par jour en mode test</p>
                    <p className="text-sm text-orange-600">Nombre de parties autorisées par jour par joueur</p>
                  </div>
                  <input
                    type="number"
                    min="1"
                    max="999"
                    value={config.test_plays_per_day || 10}
                    onChange={(e) => setConfig({...config, test_plays_per_day: parseInt(e.target.value) || 10})}
                    className="w-24 border border-orange-300 rounded px-3 py-2 text-center font-bold text-orange-800"
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Règles du jeu */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">🎯 Règles du Jeu</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-1">Temps cible (secondes)</label>
              <input
                type="number"
                step="0.01"
                min="1"
                max="60"
                value={config.target_time}
                onChange={(e) => setConfig({...config, target_time: parseFloat(e.target.value)})}
                className="border rounded px-3 py-2 w-full"
              />
              <p className="text-sm text-gray-600 mt-1">Le temps exact à atteindre (10.00 par défaut)</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Tolérance anti-triche (secondes)</label>
              <input
                type="number"
                step="0.1"
                min="0.5"
                max="2"
                value={config.tolerance}
                onChange={(e) => setConfig({...config, tolerance: parseFloat(e.target.value)})}
                className="border rounded px-3 py-2 w-full"
              />
              <p className="text-sm text-gray-600 mt-1">Écart max client/serveur (1.0s recommandé)</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Tolérance pour gagner (secondes)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="1"
                value={config.win_tolerance ?? 0.1}
                onChange={(e) => { const v = parseFloat(e.target.value); setConfig({...config, win_tolerance: isNaN(v) ? 0 : v}); }}
                className="border rounded px-3 py-2 w-full"
              />
              <p className="text-sm text-gray-600 mt-1">Marge pour gagner (0.1 = ±0.1s autour de 10.00). <strong>0 = 10s pile exact</strong> (très difficile : il faut tomber pile à 10.00).</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Tentatives par jour</label>
              <input
                type="number"
                min="1"
                value={config.chances_per_day}
                onChange={(e) => setConfig({...config, chances_per_day: parseInt(e.target.value)})}
                className="border rounded px-3 py-2 w-full"
              />
              <p className="text-sm text-gray-600 mt-1">Nombre de parties qu'un client peut jouer par jour</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">🎁 Gains possibles par jour</label>
              <input
                type="number"
                min="1"
                value={config.wins_per_day || 1}
                onChange={(e) => setConfig({...config, wins_per_day: parseInt(e.target.value) || 1})}
                className="border rounded px-3 py-2 w-full"
              />
              <p className="text-sm text-gray-600 mt-1">Nombre maximum de récompenses qu'un client peut gagner par jour (anti-abus). 1 recommandé.</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Validité de la récompense (jours)</label>
              <input
                type="number"
                min="1"
                max="30"
                value={config.reward_expiry_days}
                onChange={(e) => setConfig({...config, reward_expiry_days: parseInt(e.target.value)})}
                className="border rounded px-3 py-2 w-full"
              />
              <p className="text-sm text-gray-600 mt-1">Nombre de jours avant expiration de la récompense</p>
            </div>
          </div>

          <div className="mt-4 bg-indigo-50 border border-indigo-200 rounded-lg p-4">
            <p className="text-sm text-indigo-800">
              <strong>🛡️ Anti-triche :</strong> Le temps est validé côté serveur. Écart max client/serveur : <strong>{config.tolerance?.toFixed(1) || '1.0'}s</strong>. Pour gagner : temps entre <strong>{(config.target_time - (config.win_tolerance ?? 0.1)).toFixed(2)}s</strong> et <strong>{(config.target_time + (config.win_tolerance ?? 0.1)).toFixed(2)}s</strong>.
            </p>
          </div>
        </div>

        {/* Récompense */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">🎁 Récompense à Gagner</h2>
          <div className="space-y-4">
            {/* Type de récompense */}
            <div>
              <label className="block text-sm font-medium mb-1">Type de récompense</label>
              <select
                value={config.reward_type || 'product'}
                onChange={(e) => setConfig({...config, reward_type: e.target.value})}
                className="border rounded px-3 py-2 w-full"
              >
                <option value="product">🎁 Produit offert</option>
                <option value="wallet">💰 Crédit fidélité (€)</option>
              </select>
            </div>

            {/* Si produit offert */}
            {(config.reward_type || 'product') === 'product' && (
              <div>
                <label className="block text-sm font-medium mb-1">Produit offert</label>
                <input
                  type="text"
                  placeholder="🔍 Rechercher un produit..."
                  value={searchProduct}
                  onChange={(e) => setSearchProduct(e.target.value)}
                  className="border rounded px-3 py-2 w-full mb-2"
                />
                <select
                  value={config.reward?.product_id || ''}
                  onChange={(e) => handleProductChange(e.target.value)}
                  className="border rounded px-3 py-2 w-full"
                >
                  <option value="">-- Sélectionner un produit ({filteredProducts.length}) --</option>
                  {filteredProducts.map(product => (
                    <option key={product.id} value={product.id}>
                      {product.name} - {(product.base_price || product.price || 0).toFixed(2)}€
                    </option>
                  ))}
                </select>
                <p className="text-sm text-gray-600 mt-1">Le produit que le gagnant recevra gratuitement</p>
              </div>
            )}

            {/* Si crédit wallet */}
            {config.reward_type === 'wallet' && (
              <div>
                <label className="block text-sm font-medium mb-1">Montant à créditer (€)</label>
                <input
                  type="number"
                  step="0.5"
                  min="0.5"
                  max="50"
                  value={config.wallet_amount || 5}
                  onChange={(e) => setConfig({...config, wallet_amount: parseFloat(e.target.value)})}
                  className="border rounded px-3 py-2 w-48"
                />
                <p className="text-sm text-gray-600 mt-1">Le montant sera ajouté au solde fidélité du gagnant</p>
              </div>
            )}

            {/* Récapitulatif récompense */}
            {(config.reward_type || 'product') === 'product' && config.reward?.product_name && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="font-medium text-green-800">
                  🎁 Récompense : {config.reward.product_name}
                </p>
                <p className="text-sm text-green-600">
                  Valeur : {config.reward.value?.toFixed(2)}€
                </p>
              </div>
            )}

            {config.reward_type === 'wallet' && config.wallet_amount > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="font-medium text-yellow-800">
                  💰 Récompense : +{config.wallet_amount?.toFixed(2)}€ de crédit fidélité
                </p>
                <p className="text-sm text-yellow-600">
                  Crédité instantanément sur le compte du gagnant
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-1">Montant minimum de commande (€)</label>
              <input
                type="number"
                step="0.5"
                min="0"
                value={config.min_order_amount}
                onChange={(e) => setConfig({...config, min_order_amount: parseFloat(e.target.value)})}
                className="border rounded px-3 py-2 w-48"
              />
              <p className="text-sm text-gray-600 mt-1">Montant minimum pour utiliser la récompense (0 = pas de minimum)</p>
            </div>
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h3 className="font-medium text-yellow-800 mb-2">📝 Comment ça marche ?</h3>
          <ul className="text-sm text-yellow-700 space-y-1">
            <li>• Le joueur appuie sur START pour lancer le chronomètre</li>
            <li>• Il doit appuyer sur STOP pile à {config.target_time?.toFixed(2) || '10.00'} secondes</li>
            <li>• S'il réussit (±{config.win_tolerance?.toFixed(2) || '0.10'}s autour de {config.target_time?.toFixed(2)}s), il gagne la récompense</li>
            <li>• Il a {config.chances_per_day || 3} tentative(s) par jour</li>
            <li>• Il peut gagner au maximum {config.wins_per_day || 1} récompense(s) par jour (au-delà, ses victoires ne donnent plus de cadeau)</li>
            <li>• Les récompenses sont stockées dans "Mes cadeaux" (même système que Surprise du Jour)</li>
            <li>• Pas de limite de récompenses en attente (contrairement à Surprise du Jour)</li>
          </ul>
        </div>

        {/* Save Button - avec feedback amélioré */}
        <div className="flex justify-end gap-4 items-center">
          {saved && (
            <span className="text-green-600 font-medium animate-pulse">
              ✅ Configuration sauvegardée !
            </span>
          )}
          <button
            onClick={handleSave}
            disabled={saving || saved}
            className={`px-6 py-3 rounded-lg font-medium transition-all ${
              saved
                ? 'bg-green-500 text-white cursor-default'
                : saving
                  ? 'bg-gray-400 text-white cursor-wait'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
            }`}
          >
            {saving ? '⏳ Enregistrement...' : saved ? '✅ Enregistré !' : '💾 Enregistrer la configuration'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChronoGameConfig;
